import { getLicenseGroups } from '../../utils';
import type { RevisionLicense } from '../../../models/revisionlicense/types';

/**
 * Returns an object containing the normalized strings and license count.
 * Note that the licenses within each group are sorted and joined with ' OR ' to ensure consistent output.
 * The groups themselves are also sorted to ensure consistent ordering of the output string.
 * Return 'No License Found' if result is empty.
 * @param {RevisionLicense[]} licenses - An array of RevisionLicense objects representing the licenses to be normalized.
 * @param maxLicenses
 * @returns {string} result.partialLicenseText - A string representing the normalized licenses, grouped by license group ID, limited by maxLicenses.
 * @returns {string} result.fullLicenseText - A string representing the normalized licenses, grouped by license group ID.
 * @returns {number} result.licenseCount - The total number of license groups.
 */
export function getNormalizedLicense(
  licenses: Array<Pick<RevisionLicense, 'licenseGroupId' | 'licenseId'>>,
  maxLicenses: number = 3,
): {
  partialLicenseText: string;
  fullLicenseText: string;
  licenseCount: number;
} {
  const licenseGroups = getLicenseGroups(licenses) || [];
  const licenseGroupTexts = licenseGroups.map(group => group.text);
  const partialLicenseGroupTexts = licenseGroupTexts.slice(0, maxLicenses);

  return {
    partialLicenseText: partialLicenseGroupTexts.sort().join(', ') || 'No License Found',
    fullLicenseText: licenseGroupTexts.sort().join(', ') || 'No License Found',
    licenseCount: licenseGroupTexts.length,
  };
}
